import React, { FC, KeyboardEvent } from 'react';
import {
  CopyBlueRectangleIcon,
  MailIcon,
  OutlinedInfoIcon,
  PhoneIcon,
  PhoneIcon2,
  TimeIcon,
  WarningIcon,
  WebsiteIcon
} from 'assets/icons';
import { useAppDispatch } from 'hooks';
import { NotifierTypes } from 'constants/notifierConstants';
import { copyToClipBoard } from 'utils/commonUtils';
import { showNotification } from 'utils/Notifier';
import { CommunicationsDetailRow, CommunicationsDetails } from '../communications.model';
import { getCommunicationDetailHref, getValidCommunicationDetailRows, openCommunicationContact } from '../utils';

interface CommunicationDetailsPageProps {
  details: CommunicationsDetails;
}

const handleActivateKeyDown = (event: KeyboardEvent, action: () => void) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    action();
  }
};

const CommunicationDetailsPage: FC<CommunicationDetailsPageProps> = ({ details }) => {
  const dispatch = useAppDispatch();
  const rows = getValidCommunicationDetailRows(details);
  const regularRows = rows.filter(row => !row.highlighted);
  const highlightedRows = rows.filter(row => row.highlighted);
  const warningText = (details.warning ?? [])
    .map(line => line.trim())
    .filter(Boolean)
    .join(' ');
  const note = details.note?.trim();
  const contact = details.contact?.value?.trim() ? details.contact : null;
  const isEmailContact = Boolean(contact && (contact.type === 'EMAIL' || contact.value.includes('@')));
  const title = details.title?.trim() || 'Payment details';

  const handleCopy = (row: CommunicationsDetailRow) => {
    const value = row.value.trim();
    const label = row.label.trim();
    if (!value) return;
    if (!copyToClipBoard(value)) return;
    showNotification(
      dispatch,
      NotifierTypes.SUCCESS,
      `${label} copied`,
      3000,
      `communications-detail-copied-${label}-${Date.now()}`
    );
  };

  const handleContactClick = () => {
    openCommunicationContact(contact);
  };

  return (
    <div className="flex flex-col gap-5 pb-6" role="region" aria-label={title}>
      <div className="flex flex-col gap-5">
        {details.heading?.trim() || details.amount?.trim() || details.subheading?.trim() ? (
          <div>
            {details.heading?.trim() ? (
              <div className="text-17px font-semibold text-heading">{details.heading.trim()}</div>
            ) : null}
            {details.amount?.trim() ? (
              <div className="mt-1 text-[32px] font-semibold leading-10 text-heading">{details.amount.trim()}</div>
            ) : null}
            {details.subheading?.trim() ? (
              <div className="mt-1 text-sbase text-primaryText">{details.subheading.trim()}</div>
            ) : null}
          </div>
        ) : null}

        {warningText ? (
          <div className="flex gap-2 rounded-lg bg-[#FFF6E8] p-3">
            <WarningIcon className="mt-0.5 h-5 w-5 shrink-0 [&_path]:stroke-[#F2994A]" aria-hidden="true" />
            <p className="text-sbase font-semibold text-heading">{warningText}</p>
          </div>
        ) : null}

        {details.instruction?.trim() ? (
          <p className="text-sbase text-primaryText">{details.instruction.trim()}</p>
        ) : null}

        {regularRows.length > 0 ? (
          <div className="overflow-hidden rounded-lg border border-borderGray">
            {regularRows.map(row => {
              const href = getCommunicationDetailHref(row);
              const label = row.label.trim();
              const value = row.value.trim();
              const handleRowCopy = () => handleCopy(row);
              let linkIcon = null;
              if (row.linkType === 'PHONE') linkIcon = <PhoneIcon2 className="h-4 w-4" aria-hidden="true" />;
              else if (row.linkType === 'EMAIL') linkIcon = <MailIcon className="h-4 w-4" aria-hidden="true" />;
              else if (row.linkType === 'URL') {
                linkIcon = <WebsiteIcon className="h-4 w-4 [&_path]:fill-secondary" aria-hidden="true" />;
              }

              return (
                <div
                  key={`${label}-${value}`}
                  className="flex items-center gap-3 border-b border-borderGray px-4 py-3 last:border-b-0">
                  {linkIcon ? (
                    <span
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#E8F2FF]"
                      aria-hidden="true">
                      {linkIcon}
                    </span>
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <div className="text-px13 text-accentText">{label}</div>
                    {href ? (
                      <a
                        href={href}
                        className="mt-0.5 block break-all text-17px font-semibold text-secondary"
                        {...(row.linkType === 'URL' ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                        {value}
                      </a>
                    ) : (
                      <div className="mt-0.5 break-all text-17px font-semibold text-heading">{value}</div>
                    )}
                  </div>
                  {row.copyable ? (
                    <button
                      type="button"
                      className="shrink-0 cursor-pointer rounded p-1"
                      aria-label={`Copy ${label}`}
                      tabIndex={0}
                      onClick={handleRowCopy}
                      onKeyDown={event => handleActivateKeyDown(event, handleRowCopy)}>
                      <CopyBlueRectangleIcon className="h-[18px] w-4" aria-hidden="true" />
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}

        {highlightedRows.map((row, index) => {
          const label = row.label.trim();
          const value = row.value.trim();
          const badge = row.badge?.trim();
          const handleRowCopy = () => handleCopy(row);
          const showNote = Boolean(note) && index === highlightedRows.length - 1;

          return (
            <div key={`${label}-${value}`} className="rounded-lg border border-[#C5D9F2] bg-[#F4F8FD] p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sbase font-semibold text-heading">{label}</span>
                    {badge ? (
                      <span
                        className="inline-flex rounded-full bg-secondary px-2 py-0.5
                          text-px13 font-semibold text-white">
                        {badge}
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-1 break-all text-xl font-semibold text-heading">{value}</div>
                </div>
                {row.copyable ? (
                  <button
                    type="button"
                    className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md
                      border border-secondary px-3 py-1.5 text-sbase font-semibold text-secondary"
                    aria-label={`Copy ${label}`}
                    tabIndex={0}
                    onClick={handleRowCopy}
                    onKeyDown={event => handleActivateKeyDown(event, handleRowCopy)}>
                    <CopyBlueRectangleIcon className="h-4 w-4" aria-hidden="true" />
                    Copy
                  </button>
                ) : null}
              </div>
              {showNote ? (
                <div className="mt-3 flex gap-2 border-t border-[#D6E6F5] pt-3">
                  <OutlinedInfoIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                  <p className="text-sbase text-primaryText">{note}</p>
                </div>
              ) : null}
            </div>
          );
        })}

        {note && highlightedRows.length === 0 ? <p className="text-sbase text-primaryText">{note}</p> : null}

        {details.timing?.trim() ? (
          <div className="flex gap-2 text-sbase text-accentText">
            <TimeIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <p>{details.timing.trim()}</p>
          </div>
        ) : null}

        {contact ? (
          <>
            <div className="h-px bg-borderGray" />
            <div className="flex justify-center">
              <button
                type="button"
                className="flex w-fit cursor-pointer items-center gap-2 text-sbase text-secondary"
                aria-label={contact.label || contact.value}
                tabIndex={0}
                onClick={handleContactClick}
                onKeyDown={event => handleActivateKeyDown(event, handleContactClick)}>
                {isEmailContact ? <MailIcon className="h-4 w-4 shrink-0" aria-hidden="true" /> : null}
                {!isEmailContact && contact.type === 'PHONE' ? (
                  <PhoneIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                ) : null}
                {contact.label || contact.value}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default CommunicationDetailsPage;
